/*
 * cart-attributes.js
 *
 * Shopify 標準の注文 CSV で確認できるようにするため、現在の cart 内容から
 * order-level の cart attribute `application_data` を再構築する pure helper。
 *
 * 既存 line item properties は一切変更しない。ここでの責務は「読み取り＋要約」のみ。
 */

const APPLICATION_DATA_ATTRIBUTE_KEY = 'application_data';
const APPLICATION_DATA_VERSION = 1;

const PROP_APPLICANT_BIRTHDATE = '申込者_生年月日';
const PROP_JUDGED_AGE = '判定年齢';
const PROP_PRICE_BAND = '価格区分';
const PROP_REFERENCE_DATE = '年齢判定基準日';
const PROP_TOUR_CONSENT = 'ツアー参加規約同意';
const PROP_MINOR_CONSENT = '未成年参加_承諾';
const PROP_SEMINAR_CUSTOMER_KIND = '申込区分';
const PROP_SEMINAR_COMPANY_NAME = '会社名';

const ATTENDEE_KEY_RE = /^参加者(\d+)_(氏名|年齢|性別|電話)$/;
const AFFIRMATIVE_CONSENT_VALUES = new Set([
  '1',
  'true',
  'y',
  'yes',
  'はい',
  '同意',
  '同意済み',
  '承諾',
  '承諾済み',
]);

function toStringId(value) {
  if (value === null || value === undefined) return null;
  return String(value);
}

function toFiniteIntOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

function nonEmptyStringOrNull(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}

function normalizeConsentValue(value) {
  const normalized = nonEmptyStringOrNull(value);
  return normalized === null ? null : normalized.toLowerCase();
}

function parseConsentValue(value) {
  const normalized = normalizeConsentValue(value);
  if (normalized === null) return null;
  // Unknown non-empty values stay false so denial strings never become consent.
  return AFFIRMATIVE_CONSENT_VALUES.has(normalized);
}

function getUtf8ByteLength(value) {
  if (typeof Buffer !== 'undefined' && typeof Buffer.byteLength === 'function') {
    return Buffer.byteLength(value, 'utf8');
  }
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }
  throw new Error('Unable to measure application_data byte length');
}

function extractItemApplicationFields(item) {
  if (!item || typeof item !== 'object') return null;
  const properties = item.properties;
  if (!properties || typeof properties !== 'object') return null;

  const attendeeMap = new Map();
  let hasAnyAttendeeField = false;

  for (const rawKey of Object.keys(properties)) {
    const match = ATTENDEE_KEY_RE.exec(rawKey);
    if (!match) continue;
    const index = parseInt(match[1], 10);
    const field = match[2];
    if (!attendeeMap.has(index)) {
      attendeeMap.set(index, { index, name: null, age: null, gender: null, tel: null });
    }
    const entry = attendeeMap.get(index);
    const value = properties[rawKey];
    if (field === '氏名') entry.name = nonEmptyStringOrNull(value);
    else if (field === '年齢') entry.age = toFiniteIntOrNull(value);
    else if (field === '性別') entry.gender = nonEmptyStringOrNull(value);
    else if (field === '電話') entry.tel = nonEmptyStringOrNull(value);
    hasAnyAttendeeField = true;
  }

  const attendees = Array.from(attendeeMap.values())
    .sort((a, b) => a.index - b.index)
    .filter((entry) => entry.name !== null);

  const applicantBirthdate = nonEmptyStringOrNull(properties[PROP_APPLICANT_BIRTHDATE]);
  const judgedAge = toFiniteIntOrNull(properties[PROP_JUDGED_AGE]);
  const priceBand = nonEmptyStringOrNull(properties[PROP_PRICE_BAND]);
  const referenceDate = nonEmptyStringOrNull(properties[PROP_REFERENCE_DATE]);
  const rawTourConsent = nonEmptyStringOrNull(properties[PROP_TOUR_CONSENT]);
  const rawMinorConsent = nonEmptyStringOrNull(properties[PROP_MINOR_CONSENT]);
  const seminarCustomerKind = nonEmptyStringOrNull(properties[PROP_SEMINAR_CUSTOMER_KIND]);
  const seminarCompanyName = nonEmptyStringOrNull(properties[PROP_SEMINAR_COMPANY_NAME]);

  const hasApplicant = applicantBirthdate || judgedAge !== null || priceBand || referenceDate;
  const hasTourConsentProp = Object.prototype.hasOwnProperty.call(properties, PROP_TOUR_CONSENT);
  const hasMinorConsentProp = Object.prototype.hasOwnProperty.call(properties, PROP_MINOR_CONSENT);
  const hasSeminarData = seminarCustomerKind !== null || seminarCompanyName !== null;

  if (!hasAnyAttendeeField && !hasApplicant && !hasTourConsentProp && !hasMinorConsentProp && !hasSeminarData) {
    return null;
  }

  return {
    variant_id: toStringId(item.variant_id ?? item.id),
    product_id: toStringId(item.product_id),
    product_title: nonEmptyStringOrNull(item.product_title),
    quantity: toFiniteIntOrNull(item.quantity) ?? 0,
    applicant: hasApplicant
      ? {
          birthdate: applicantBirthdate,
          judged_age: judgedAge,
          price_band: priceBand,
          reference_date: referenceDate,
        }
      : null,
    attendees,
    consents: {
      tour: hasTourConsentProp ? parseConsentValue(rawTourConsent) : null,
      minor: hasMinorConsentProp ? parseConsentValue(rawMinorConsent) : null,
    },
    seminar: hasSeminarData
      ? {
          customer_kind: seminarCustomerKind,
          company_name: seminarCompanyName,
        }
      : null,
  };
}

function isMinorAge(age) {
  return Number.isFinite(age) && age >= 0 && age < 18;
}

function buildApplicationData(cart, options = {}) {
  const opts = options ?? {};
  const nowFn = typeof opts.now === 'function' ? opts.now : () => new Date();
  const nowDate = nowFn();
  const updatedAt = nowDate instanceof Date && !Number.isNaN(nowDate.getTime())
    ? nowDate.toISOString()
    : new Date().toISOString();

  const rawItems = cart && Array.isArray(cart.items) ? cart.items : [];
  const items = [];
  let totalAttendees = 0;
  let hasMinorAttendee = false;
  let itemsWithTour = 0;
  let tourConsentPassed = 0;
  let itemsWithMinor = 0;
  let minorConsentPassed = 0;

  for (const raw of rawItems) {
    const fields = extractItemApplicationFields(raw);
    if (!fields) continue;

    totalAttendees += fields.attendees.length;
    const itemHasMinor = fields.attendees.some((a) => isMinorAge(a.age));
    if (itemHasMinor) {
      hasMinorAttendee = true;
      itemsWithMinor += 1;
      if (fields.consents.minor === true) minorConsentPassed += 1;
    }
    if (fields.consents.tour !== null && fields.consents.tour !== undefined) {
      itemsWithTour += 1;
    }
    if (fields.consents.tour === true) tourConsentPassed += 1;

    items.push(fields);
  }

  return {
    version: APPLICATION_DATA_VERSION,
    updated_at: updatedAt,
    summary: {
      total_items: items.length,
      total_attendees: totalAttendees,
      has_minor_attendee: hasMinorAttendee,
      all_tour_consent: itemsWithTour === 0 ? true : tourConsentPassed === itemsWithTour,
      all_minor_consent: itemsWithMinor === 0 ? true : minorConsentPassed === itemsWithMinor,
    },
    items,
  };
}

function serializeApplicationData(cart, options = {}) {
  const opts = options ?? {};
  const { maxBytes = undefined, ...buildOptions } = opts;
  const serialized = JSON.stringify(buildApplicationData(cart, buildOptions));

  if (maxBytes === undefined) return serialized;
  if (!Number.isInteger(maxBytes) || maxBytes < 0) {
    throw new TypeError('serializeApplicationData maxBytes must be a non-negative integer');
  }

  const byteLength = getUtf8ByteLength(serialized);
  if (byteLength > maxBytes) {
    const error = new Error(`application_data exceeds maxBytes: ${byteLength} > ${maxBytes}`);
    error.code = 'APPLICATION_DATA_OVERFLOW';
    error.byteLength = byteLength;
    error.maxBytes = maxBytes;
    throw error;
  }

  return serialized;
}

const cartAttributesExports = {
  APPLICATION_DATA_ATTRIBUTE_KEY,
  APPLICATION_DATA_VERSION,
  extractItemApplicationFields,
  buildApplicationData,
  serializeApplicationData,
};

if (typeof module !== 'undefined' && module.exports) {
  module.exports = cartAttributesExports;
}

if (typeof window !== 'undefined') {
  window.MFCartAttributes = Object.assign({}, window.MFCartAttributes, cartAttributesExports);
}
